import React, { FC } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from "recharts";
import { Box, Text } from "@chakra-ui/react";
import { MonthlyRevenuePoint } from "../types";
import { formatNairaAxis } from "../format";
import { formatAsNaira } from "../../../helpers/helpers";

interface RevenueChartProps {
  data: MonthlyRevenuePoint[];
}

const BAR_FILL = "#003952";
const BAR_FILL_HOVER = "#ED6D5C";

const ChartTooltip: FC<TooltipProps<number, string>> = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload as MonthlyRevenuePoint;

  return (
    <Box
      bg="white"
      borderWidth="1px"
      borderColor="primary.150"
      borderRadius="md"
      px={3}
      py={2}
      boxShadow="md"
      minWidth="160px"
    >
      <Text fontSize="xs" textTransform="uppercase" letterSpacing="wider" color="primary.300">
        {point.monthLabel}
      </Text>
      <Text mt={1} fontWeight="semibold" color="primary.500">
        {formatAsNaira(point.total)}
      </Text>
    </Box>
  );
};

export const RevenueChart: FC<RevenueChartProps> = ({ data }) => {
  const maxTotal = Math.max(...data.map((point) => point.total), 0);
  const peakIndex = maxTotal > 0 ? data.findIndex((point) => point.total === maxTotal) : -1;

  return (
    <Box width="100%" height={340}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          margin={{ top: 24, right: 12, left: 12, bottom: 8 }}
          barCategoryGap="22%"
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5EBEE" />
          <XAxis
            dataKey="monthLabel"
            stroke="#7c95a0"
            fontSize={12}
            tickLine={false}
            axisLine={{ stroke: "#D9E1E5" }}
            dy={6}
          />
          <YAxis
            stroke="#7c95a0"
            fontSize={12}
            tickLine={false}
            axisLine={false}
            tickFormatter={(value: number) => formatNairaAxis(value)}
            width={70}
          />
          <Tooltip cursor={{ fill: "rgba(237, 109, 92, 0.08)" }} content={<ChartTooltip />} />
          <Bar dataKey="total" radius={[6, 6, 0, 0]}>
            {data.map((entry, index) => (
              <Cell
                key={entry.monthKey}
                fill={index === peakIndex ? BAR_FILL_HOVER : BAR_FILL}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};
